/*
 * newsbot - Form error notification component
 * Displays form validation errors at the top of a form
 */

import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { useFormikContext, FormikErrors } from "formik";

interface FormErrorNotificationProps {
  title?: string;
}

export function FormErrorNotification({ title = "Please fix the following errors:" }: FormErrorNotificationProps) {
  const { errors, touched, submitCount } = useFormikContext<Record<string, unknown>>();

  // Only show errors after form has been submitted at least once
  if (submitCount === 0) return null;

  const errorMessages = getErrorMessages(errors, touched);

  if (errorMessages.length === 0) return null;

  return (
    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
      <div className="flex">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0" />
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{title}</h3>
          <ul className="mt-2 text-sm text-red-700 dark:text-red-300 list-disc list-inside">
            {errorMessages.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function getErrorMessages(
  errors: FormikErrors<Record<string, unknown>>,
  touched: Record<string, boolean | undefined>
): string[] {
  const messages: string[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (touched[key] && typeof value === "string") {
      messages.push(value);
    }
  }

  return messages;
}
